import type { ReactNode } from "react";
import { useTranslation } from "src/i18n";
import { Button, Color, Flex, Text } from "src/elements";
import { styledSafe } from "src/utils/styledComponent";

const Column = styledSafe("div")`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
`;

const Body = styledSafe("div")`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
  overflow: hidden;
`;

const Drawer = styledSafe("div")`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: ${Color.white};
  animation: o-drawer-in 180ms ease-out;

  @keyframes o-drawer-in {
    from {
      transform: translateX(100%);
    }
    to {
      transform: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const LineLink = styledSafe("button")`
  padding: 0;
  border: none;
  background: none;
  color: ${Color.primary};
  font: inherit;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

interface Props {
  advanced: ReactNode;
  canGenerate: boolean;
  isGenerating: boolean;
  isGenerateDisabled: boolean;
  generateTitle?: string;
  /** Names the blocking mode and the CDT the run will use. */
  summary?: string;
  /** Why Generate is off, shown beside it. */
  blockedReason?: string;
  /** The way past the refusal, beside its reason (Open CDT Studio). */
  blockedAction?: { label: string; onClick: () => void };

  copiesBase: boolean;
  onStopCopying: () => void;

  asksFavourite: boolean;
  onCancel: () => void;
  onGenerate: () => void;
  onFavouriteAndGenerate: () => void;

  children: ReactNode;
}

export const OFrontDoor = ({
  advanced,
  canGenerate,
  isGenerating,
  isGenerateDisabled,
  generateTitle,
  summary,
  blockedReason,
  blockedAction,
  copiesBase,
  onStopCopying,
  asksFavourite,
  onCancel,
  onGenerate,
  onFavouriteAndGenerate,
  children,
}: Props) => {
  const { t } = useTranslation("common");
  const isAdvanced = advanced !== null && advanced !== undefined;

  return (
    <Column>
      <Body>
        <Column inert={isAdvanced} aria-hidden={isAdvanced || undefined}>
          {children}
        </Column>

        {isAdvanced && (
          <Drawer role="region" aria-label={t("configCopilot.advanced")} data-testid="o-drawer">
            {advanced}
          </Drawer>
        )}
      </Body>

      <Flex
        column
        gap="10px"
        padding="12px 20px"
        background={Color.lightGray}
        borderTop={`1px solid ${Color.lightGrayHover}`}
      >
        {canGenerate && copiesBase && (
          <Flex gap="6px" align="center" data-testid="o-copies-base">
            <Text variant="small1" color={Color.textSecondary}>
              {t("configCopilot.copiesBase")}
            </Text>
            <LineLink type="button" onClick={onStopCopying}>
              <Text variant="small1" color={Color.primary}>
                {t("configCopilot.turnOff")}
              </Text>
            </LineLink>
          </Flex>
        )}

        {canGenerate && summary && (
          <Text variant="small1" color={Color.textSecondary} testId="o-blocking-summary">
            {summary}
          </Text>
        )}

        {blockedReason && (
          <Flex gap="8px" align="center">
            <Text variant="small1" color={Color.red} testId="rules-blocked-reason">
              {blockedReason}
            </Text>
            {blockedAction && (
              <Button
                name="rules-blocked-action"
                size="small"
                color="primary"
                variant="inverted"
                onClick={blockedAction.onClick}
              >
                {blockedAction.label}
              </Button>
            )}
          </Flex>
        )}

        <Flex justify="between" align="center" gap="12px">
          <Text variant="small1" color={Color.textSecondary}>
            {canGenerate && asksFavourite ? t("configCopilot.favouriteFirst") : ""}
          </Text>

          <Flex gap="12px">
            <Button name="rules-cancel" color="primary" variant="inverted" onClick={onCancel}>
              {t("configCopilot.cancel")}
            </Button>
            {canGenerate && asksFavourite && (
              <>
                <Button
                  color="primary"
                  variant="inverted"
                  onClick={onGenerate}
                  isDisabled={isGenerateDisabled}
                  title={generateTitle}
                >
                  {t("configCopilot.onlyGenerate")}
                </Button>
                <Button
                  name="rules-generate"
                  color="primary"
                  onClick={onFavouriteAndGenerate}
                  isDisabled={isGenerateDisabled}
                  title={generateTitle}
                  isLoading={isGenerating}
                >
                  {t("configCopilot.favouriteAndGenerate")}
                </Button>
              </>
            )}
            {canGenerate && !asksFavourite && (
              <Button
                name="rules-generate"
                color="primary"
                onClick={onGenerate}
                isDisabled={isGenerateDisabled}
                title={generateTitle}
                isLoading={isGenerating}
              >
                {t("configCopilot.generate")}
              </Button>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Column>
  );
};
